import React, { Component, createRef, forwardRef } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { List, type RenderedRows } from 'react-virtualized/dist/commonjs/List';
import { WindowScroller } from 'react-virtualized/dist/commonjs/WindowScroller';
import ScrollLongTaskAnalytics from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/scroll-long-task-analytics';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import Issue from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/scope/issues/issue';
import IssuesWithoutParent from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/scope/issues/issues-without-parent';
import IssuesWithoutParentHeader from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/scope/issues/issues-without-parent-header';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import {
	SCOPE_ROW_HEIGHT,
	STICKY_HEADER_CONTAINER_PREFIX,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import StickyGroup from '../sticky-group';
import type { Props, State } from './types';

// @ts-expect-error - TS2345 - Type 'undefined' is not assignable to type 'ReactElement<any, any>'.
export const SortableIssue = SortableElement(Issue);
export const SortableIssuesWithoutParent = SortableElement(IssuesWithoutParent);
// Remove with the cleanup of com.atlassian.rm.jpo.ease-of-use.remove-no-parent-header
export const SortableIssuesWithoutParentHeader = SortableElement(IssuesWithoutParentHeader);

// eslint-disable-next-line jira/react/no-class-components
export class SortableListWrapper extends Component<Props, State> {
	// @ts-expect-error - TS2564 - Property 'onChildScroll' has no initializer and is not definitely assigned in the constructor.
	// eslint-disable-next-line react/sort-comp
	onChildScroll: (arg1: { scrollTop: number }) => void;

	listRef = createRef<List>();

	state = { startIndex: undefined };

	calculateOffset = () => {
		const { scrollToIndex, heightOffset } = this.props;
		const { startIndex } = this.state;
		// we need to correct scrollIndex only when scroll to index is lower than first visible row
		if (startIndex && isDefined(scrollToIndex) && scrollToIndex < startIndex) {
			return heightOffset;
		}
		return 0;
	};

	componentDidUpdate(prevProps: Props) {
		const { scrollToIndex, rows } = this.props;
		const listRef = this.listRef.current;
		if (listRef && scrollToIndex && scrollToIndex !== prevProps.scrollToIndex) {
			const scrollTop = listRef.getOffsetForRow({ index: scrollToIndex });
			this.onChildScroll({ scrollTop: scrollTop - this.calculateOffset() });
		}
		if (listRef && prevProps.rows !== rows) {
			const length = Math.min(prevProps.rows.length, rows.length);
			for (let i = 0; i < length; i++) {
				if (prevProps.rows[i].height !== rows[i].height) {
					listRef.recomputeRowHeights();
					break;
				}
			}
		}
	}

	rowsRendered = ({ startIndex }: RenderedRows) => {
		this.setState(() => ({ startIndex }));
	};

	render() {
		const {
			dropParentId,
			isReadOnly,
			rows,
			rowRenderer,
			width = 0,
			scrollElement,
			StickyHeader,
			handleScroll,
		} = this.props;
		return (
			<WindowScroller scrollElement={scrollElement} onScroll={handleScroll}>
				{({ height = 0, isScrolling, onChildScroll, scrollTop }) => {
					this.onChildScroll = onChildScroll;

					return (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<div style={{ position: 'relative' }}>
							<StickyHeader
								sectionId="scope"
								containerId={`${STICKY_HEADER_CONTAINER_PREFIX}-scope`}
							>
								<StickyGroup />
							</StickyHeader>
							<List
								ref={this.listRef}
								autoHeight
								dropParentId={dropParentId}
								height={height}
								isReadOnly={isReadOnly}
								isScrolling={isScrolling}
								onScroll={onChildScroll}
								rowCount={rows.length}
								rowHeight={({ index }) => {
									// eslint-disable-next-line @typescript-eslint/no-shadow
									const height = rows[index].height || 1;
									return height * SCOPE_ROW_HEIGHT;
								}}
								rowRenderer={rowRenderer}
								rows={rows}
								scrollTop={scrollTop}
								tabIndex={null}
								width={width}
								onRowsRendered={this.rowsRendered}
								overscanRowCount={20}
							/>
						</div>
					);
				}}
			</WindowScroller>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SortableWrapperWithScrollTracking = forwardRef<any, any>((props: Props, ref) => {
	const { rows } = props;
	return (
		<ScrollLongTaskAnalytics
			// @ts-expect-error Argument of type 'UIAnalyticsEvent' is not assignable to parameter of type 'InteractionMetricType'.
			analyticsEvent={PRODUCT_ANALYTICS_EVENT_NAMES.TIMELINE_VERTICAL_SCROLLED}
			attributes={{ rowCount: rows.length }}
			includeFirstScroll
		>
			{(handleScroll: () => void) => (
				<SortableListWrapper {...props} handleScroll={handleScroll} ref={ref} />
			)}
		</ScrollLongTaskAnalytics>
	);
});

export const SortableList = SortableContainer(
	// @ts-expect-error - TS2345 - Type 'ReactElement<any, string | JSXElementConstructor<any>> | null' is not assignable to type 'Element'.
	SortableWrapperWithScrollTracking,
	{ withRef: true },
);
